import React from 'react';
import { SectionCard } from '../../components/sections/SectionCard';
import { ItemsTableWithSummary } from '../../components/display/ItemsTableWithSummary';
import {
  SUBSCRIPTION_UPCOMING_INVOICE_ITEMS,
  SUBSCRIPTION_UPCOMING_INVOICE_SUMMARY_ROWS
} from '@shared/data/mockSubscriptions';
import type { PanelTableColumn } from '../../components/display/PanelTable/types';
import type { TableAction } from '@shared/types/data-table';
import type { InvoiceItem } from '@/features/subscriptions/types/subscriptions/subscription.types';
import type { SummaryRow } from '../../components/display/ItemsTableWithSummary';
import type { Subscription } from '@/features/subscriptions/types/subscriptions/subscription.types';
import { toast } from 'sonner';
import { Package } from 'lucide-react';

interface UpcomingInvoiceSectionProps {
  isDetailView?: boolean;
  subscriptionData?: Subscription;
  invoiceItems?: InvoiceItem[];
  summaryRows?: SummaryRow[];
  className?: string;
}

const renderDescription = (value: unknown): React.ReactNode => {
  const description = typeof value === 'string' ? value : '';
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center justify-center size-10 rounded-lg bg-surface">
        <Package className="size-5 text-teal-300" />
      </div>
      <span className="text-sm font-medium">{description}</span>
    </div>
  );
};

const renderQuantity = (value: unknown): React.ReactNode => {
  const qty = typeof value === 'number' ? value : 0;
  return <span className="text-sm">{qty}</span>;
};

const renderPrice = (value: unknown): React.ReactNode => {
  const price = typeof value === 'string' ? value : '$0.00';
  return <span className="text-sm text-teal-300">{price}</span>;
};

const renderAmount = (value: unknown): React.ReactNode => {
  const amount = typeof value === 'string' ? value : '$0.00';
  return <span className="text-sm text-teal-300">{amount}</span>;
};

const columns: PanelTableColumn<InvoiceItem>[] = [
  {
    key: 'description',
    label: 'DESCRIPTION',
    render: renderDescription
  },
  {
    key: 'quantity',
    label: 'QTY',
    headerClassName: 'text-right w-20',
    cellClassName: 'text-right',
    render: renderQuantity
  },
  {
    key: 'unitPrice',
    label: 'UNIT PRICE',
    headerClassName: 'text-right w-[150px]',
    cellClassName: 'text-right',
    render: renderPrice
  },
  {
    key: 'amount',
    label: 'AMOUNT',
    headerClassName: 'text-right w-[150px]',
    cellClassName: 'text-right',
    render: renderAmount
  }
];

const rowActions = (_item: InvoiceItem): TableAction<InvoiceItem>[] => {
  return [
    {
      key: 'view-price-details',
      label: 'View price details',
      onClick: () => {
        toast.info('View price details')
      }
    },
    {
      key: 'view-product',
      label: 'View product',
      onClick: () => {
        toast.info('View product')
      }
    }
  ];
};

export const UpcomingInvoiceSection = React.memo<UpcomingInvoiceSectionProps>(({
  isDetailView = false,
  invoiceItems,
  summaryRows,
  className,
}) => {
  const data = invoiceItems ?? SUBSCRIPTION_UPCOMING_INVOICE_ITEMS;
  const summary = summaryRows ?? SUBSCRIPTION_UPCOMING_INVOICE_SUMMARY_ROWS;

  return (
    <SectionCard title="Upcoming invoice" layout="down" className={className}>
      <ItemsTableWithSummary<InvoiceItem>
        data={data}
        columns={columns}
        summaryRows={summary}
        variant={isDetailView ? 'full' : 'panel'}
        showPagination={false}
        rowActions={rowActions}
        emptyMessage="No upcoming invoice items"
        className="[&>div>table>tbody>tr:last-child]:border-b"
        labelColSpan={2}
      />
    </SectionCard>
  );
});

UpcomingInvoiceSection.displayName = 'UpcomingInvoiceSection';
